import type { OrderEtaPayload, OrderStatus } from "@the-drop/types";
import { WS_EVENT } from "@the-drop/types";
import { useEffect, useRef, useState } from "react";
import { Manager, type Socket } from "socket.io-client";

function normalizeWsId(raw: string): string {
  return raw.trim().replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
}

export type DriverLocationPayload = {
  lat: number;
  lng: number;
  runId?: string;
  driverId?: string;
};

export type DriverPositionsState = Record<string, { lat: number; lng: number }>;

type ApplyResult =
  | { kind: "applied"; next: DriverPositionsState; key: string }
  | { kind: "skipped"; reason: "non-finite-coords" | "missing-identity" };

/**
 * Pure reducer for `driver.location` events — extracted so the accumulation/key-derivation/skip
 * logic is testable without mocking `socket.io-client`. The hook just calls this and `setState`s.
 *
 * Rules:
 * 1. Skip if `lat`/`lng` are not finite numbers.
 * 2. Derive key from `driverId` (preferred) or `runId` (fallback). If neither, skip — never synthesize
 *    a magic-string key.
 * 3. Merge into the previous map, in-place semantics on existing keys (preserves insertion order so
 *    `Object.values(next).at(-1)` correctly identifies the most-recently-introduced driver).
 */
export function applyDriverLocationEvent(
  prev: DriverPositionsState,
  raw: unknown,
): ApplyResult {
  const p = raw as Partial<DriverLocationPayload>;
  if (
    typeof p?.lat !== "number" ||
    typeof p?.lng !== "number" ||
    !Number.isFinite(p.lat) ||
    !Number.isFinite(p.lng)
  ) {
    return { kind: "skipped", reason: "non-finite-coords" };
  }
  const key = p.driverId ?? p.runId;
  if (!key) {
    return { kind: "skipped", reason: "missing-identity" };
  }
  return {
    kind: "applied",
    next: { ...prev, [key]: { lat: p.lat, lng: p.lng } },
    key,
  };
}

export type UseDriverLocationSocketOptions = {
  /** Full Socket.io namespace path, e.g. `/ws/orders/:orderId`, `/ws/dispensary/:id`, `/ws/transporter/:id`. */
  namespace: string | null;
  getToken: () => Promise<string | null>;
  enabled: boolean;
  /**
   * Order status, only consumed when responding to **`run.completed`** so the customer-facing copy stays
   * accurate when the order is **`failed`** vs **`completed`** vs in-flight. Internal consumers
   * (dispensary, transporter) can omit; the `liveMessage` field is then less meaningful to them.
   */
  orderStatus?: OrderStatus | null;
  /** Called when **`run.completed`** fires. Customer-web uses this to silently refetch order detail. */
  onRunCompleted?: () => void;
  /**
   * Override the Socket.io host. Pass the consumer's API base (e.g. from a Vite env). Falls back to
   * **`globalThis.location.origin`** when omitted — same-origin behavior under Vite dev proxies.
   */
  apiBaseUrl?: string;
};

/**
 * Subscribes to a Socket.io namespace (orders/dispensary/transporter) with **`path: /socket.io/`** and
 * Clerk JWT in **`auth.token`**. Accumulates positions from **`driver.location`** into
 * **`driversById`**, keyed by `payload.driverId` (or `payload.runId` as fallback). Events with neither
 * are skipped + warned (malformed). Surfaces lightweight status strings for a few run events as
 * `liveMessage`.
 *
 * **Intended consumer shape:** this hook is the **per-screen scaffolding for single-driver consumers**
 * — it spins up its own socket connection and is intended for a screen that loads, listens for one
 * driver, and tears down (e.g. customer-web's order-tracking screen).
 *
 * **Fleet consumers (dispensary, transporter) do NOT use this hook.** They integrate `driver.location`
 * into their app-level data layer instead: a shared socket per app via context (e.g. dispensary's
 * `DispensarySocketProvider`), plus a per-feature aggregator that listens on it
 * (`useDispensaryDashboardLive`, `useTransporterDriversLive`). That pattern treats the API-fetched
 * drivers list as truth and the WS stream as position deltas — sidestepping the accumulation/eviction
 * concern entirely.
 *
 * **Accumulation semantics:** `driversById` is monotonically-growing within a single subscription.
 * Single-driver consumers handle this by reading the most-recently-inserted entry via
 * `Object.values(driversById).at(-1) ?? null`, which correctly reflects a mid-run driver swap (A → B).
 * Any **future** consumer that uses this hook for a fleet view would need an eviction story
 * (e.g. server-emitted `driver.offline { driverId }` + eviction here). Not a current concern.
 */
export function useDriverLocationSocket(options: UseDriverLocationSocketOptions): {
  driversById: Record<string, { lat: number; lng: number }>;
  liveMessage: string | null;
  socketConnected: boolean;
  /**
   * Most recent `order.eta` payload received on this namespace. Customer-web subscribes to its own
   * order's namespace and gets one entry; the hook simply replaces it on every event (the latest
   * compute supersedes prior windows). The customer-disclosure rule still applies — payload is
   * coordinates-free + stops-ahead-free by contract (`OrderEtaPayload` in `@the-drop/types`).
   */
  orderEta: OrderEtaPayload | null;
} {
  const { namespace, getToken, enabled, orderStatus, onRunCompleted, apiBaseUrl } = options;

  const [driversById, setDriversById] = useState<Record<string, { lat: number; lng: number }>>({});
  const [liveMessage, setLiveMessage] = useState<string | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [orderEta, setOrderEta] = useState<OrderEtaPayload | null>(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const orderStatusRef = useRef<OrderStatus | null | undefined>(orderStatus);
  orderStatusRef.current = orderStatus;
  const onRunCompletedRef = useRef(onRunCompleted);
  onRunCompletedRef.current = onRunCompleted;

  useEffect(() => {
    if (!enabled || !namespace) {
      setSocketConnected(false);
      return;
    }

    const ns = normalizeWsId(namespace);
    const socketRef: { current: Socket | null } = { current: null };
    let cancelled = false;

    void (async () => {
      const token = await getTokenRef.current();
      if (cancelled || !token) return;

      const base = (apiBaseUrl || globalThis.location?.origin || "").replace(/\/$/, "");
      const manager = new Manager(base, {
        path: "/socket.io/",
        autoConnect: false,
        transports: ["websocket", "polling"],
        reconnectionAttempts: 8,
        reconnectionDelay: 1200,
      });
      const socket = manager.socket(ns, { auth: { token } });
      if (cancelled) {
        socket.disconnect();
        return;
      }
      socketRef.current = socket;

      socket.on("connect", () => {
        if (!cancelled) setSocketConnected(true);
      });
      socket.on("disconnect", () => {
        if (!cancelled) setSocketConnected(false);
      });
      socket.on("connect_error", () => {
        if (!cancelled) setSocketConnected(false);
      });

      // `DRIVER_LOCATION` listener intentionally kept after the Phase-1 compliance fix.
      // The customer namespace is gated server-side by `CUSTOMER_NAMESPACE_DENYLIST` + the
      // `emitToOrderNamespace` helper (see `services/api/src/ws/`) — neither this socket nor
      // any other customer-side listener should ever see this event in prod. Kept as
      // defense-in-depth: if a future server regression starts emitting again, the client
      // accumulates state quietly into `driversById` without rendering anything (customer-web
      // doesn't read `driversById` post-Phase-1), and the server-side capture in Sentry will
      // page ops first.
      socket.on(WS_EVENT.DRIVER_LOCATION, (raw: unknown) => {
        setDriversById((prev) => {
          const result = applyDriverLocationEvent(prev, raw);
          if (result.kind === "skipped" && result.reason === "missing-identity") {
            // Malformed payload — driver.location must carry at least one identity field. We don't
            // synthesize a magic-string key (would collide with future legit ids) — skip + warn.
            console.warn("driver.location event missing driverId and runId; skipping", raw);
          }
          return result.kind === "applied" ? result.next : prev;
        });
      });

      socket.on(WS_EVENT.DRIVER_ETA_UPDATE, () => {
        setLiveMessage("Driver is heading to the dispensary.");
      });
      socket.on(WS_EVENT.RUN_ARRIVED_AT_DISPENSARY, () => {
        setLiveMessage("Driver arrived at the dispensary.");
      });
      socket.on(WS_EVENT.RUN_COMPLETED, () => {
        onRunCompletedRef.current?.();
        const s = orderStatusRef.current;
        if (s === "failed") {
          setLiveMessage("Courier finished this route. Your order was not delivered.");
        } else if (s === "cancelled") {
          setLiveMessage("Courier finished this route.");
        } else if (s === "completed") {
          setLiveMessage("Your order was delivered. Thank you.");
        } else {
          setLiveMessage("Courier finished the delivery route.");
        }
      });

      // Customer-safe ETA window. Comes through PR #375's orchestrator (driver-action triggers in
      // PR #376) + the 1-min running-late tick + the 5-min recompute tick (both PR #377). Payload
      // is coordinates-free + stops-ahead-free by contract — `OrderEtaPayload` in
      // `@the-drop/types`. Latest payload wins; stale prior payloads are overwritten.
      socket.on(WS_EVENT.ORDER_ETA, (raw: unknown) => {
        const p = raw as Partial<OrderEtaPayload> | null;
        if (
          p == null ||
          typeof p.orderId !== "string" ||
          typeof p.etaStart !== "string" ||
          typeof p.etaEnd !== "string" ||
          typeof p.computedAt !== "string"
        ) {
          // Malformed payload — skip rather than show a broken window.
          return;
        }
        setOrderEta({
          orderId: p.orderId,
          etaStart: p.etaStart,
          etaEnd: p.etaEnd,
          computedAt: p.computedAt,
          runningLate: p.runningLate === true,
        });
      });

      socket.connect();
    })();

    return () => {
      cancelled = true;
      const s = socketRef.current;
      if (s) {
        s.removeAllListeners();
        s.disconnect();
        socketRef.current = null;
      }
      setSocketConnected(false);
    };
  }, [enabled, namespace, apiBaseUrl]);

  return { driversById, liveMessage, socketConnected, orderEta };
}
